import { useEffect, useState } from 'react';

/**
 * A router, because the app has six screens and one deep link.
 *
 * `/t/:id` matters: it is the URL a routine puts in its brief and the one that
 * goes into a PR comment, so a link to a task has to survive being pasted into
 * Slack. Everything else is navigation, and navigation does not need a library.
 */

/**
 * The two protocol pages, addressed by the same word `brief.ts` uses for the
 * two token kinds: a **task** token, scoped to one dispatch, and an **agent**
 * token, standing across a routine's whole life. `/protocol` is public — no
 * session required, see App.tsx — because it is the page a project's own
 * CLAUDE.md points at, and a routine reading its own instructions has no
 * session to offer.
 */
export type ProtocolVariant = 'task' | 'agent';

export type Route =
  | { name: 'tasks'; taskId: string | null }
  | { name: 'suggested'; followUpId: string | null }
  | { name: 'overview' }
  | { name: 'repos' }
  | { name: 'agents' }
  | { name: 'intakes' }
  | { name: 'people' }
  | { name: 'settings' }
  | { name: 'protocol'; variant: ProtocolVariant }
  | { name: 'api-docs' };

export function parseRoute(pathname: string): Route {
  const parts = pathname.replace(/^\/+|\/+$/g, '').split('/');
  const head = parts[0] ?? '';

  switch (head) {
    case 't':
      return { name: 'tasks', taskId: parts[1] ?? null };
    case 'suggested':
      return { name: 'suggested', followUpId: parts[1] ?? null };
    case 'overview':
      return { name: 'overview' };
    case 'repos':
      return { name: 'repos' };
    case 'agents':
      return { name: 'agents' };
    case 'intakes':
      return { name: 'intakes' };
    case 'people':
      return { name: 'people' };
    case 'settings':
      return { name: 'settings' };
    case 'protocol':
      return { name: 'protocol', variant: parts[1] === 'agent' ? 'agent' : 'task' };
    case 'api-docs':
      return { name: 'api-docs' };
    default:
      return { name: 'tasks', taskId: null };
  }
}

export function navigate(path: string, replace = false): void {
  if (replace) window.history.replaceState({}, '', path);
  else window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  useEffect(() => {
    const onChange = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener('popstate', onChange);
    return () => window.removeEventListener('popstate', onChange);
  }, []);

  return route;
}
